import {
  KeyboardEvent,
  PointerEvent as ReactPointerEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Maximize,
  Minimize,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface OnboardingVideoPlayerProps {
  src: string;
  title: string;
  className?: string;
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Themed player for the onboarding walkthrough clips. Native controls are
 * off so there is no download/picture-in-picture menu; play, seek, mute and
 * fullscreen are drawn with the brand tokens instead. A large brand play
 * button sits over the frame whenever the clip is paused.
 */
export function OnboardingVideoPlayer({
  src,
  title,
  className,
}: OnboardingVideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<number | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);

  const progress = duration > 0 ? Math.min(currentTime / duration, 1) : 0;
  const bufferedRatio = duration > 0 ? Math.min(buffered / duration, 1) : 0;

  // Reset per clip.
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setBuffered(0);
    setControlsVisible(true);
  }, [src]);

  useEffect(() => {
    const onFsChange = () =>
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const scheduleHide = useCallback(() => {
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setControlsVisible(false), 2200);
  }, []);

  const revealControls = useCallback(() => {
    setControlsVisible(true);
    if (isPlaying) scheduleHide();
  }, [isPlaying, scheduleHide]);

  useEffect(() => {
    if (isPlaying) scheduleHide();
    else {
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
      setControlsVisible(true);
    }
    return () => {
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
    };
  }, [isPlaying, scheduleHide]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused || video.ended) {
      void video.play().catch(() => {
        /* autoplay policy or aborted load; overlay stays visible */
      });
    } else {
      video.pause();
    }
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement === el) {
      void document.exitFullscreen();
    } else if (el.requestFullscreen) {
      void el.requestFullscreen();
    }
  }, []);

  const seekTo = useCallback(
    (ratio: number) => {
      const video = videoRef.current;
      if (!video || !duration) return;
      const next = Math.max(0, Math.min(1, ratio)) * duration;
      video.currentTime = next;
      setCurrentTime(next);
    },
    [duration]
  );

  const seekBy = useCallback(
    (delta: number) => {
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = Math.max(
        0,
        Math.min(video.duration || 0, video.currentTime + delta)
      );
    },
    []
  );

  const ratioFromPointer = (e: ReactPointerEvent<HTMLDivElement>) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    return (e.clientX - rect.left) / rect.width;
  };

  const handleTrackPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsScrubbing(true);
    seekTo(ratioFromPointer(e));
  };

  const handleTrackPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!isScrubbing) return;
    seekTo(ratioFromPointer(e));
  };

  const handleTrackPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!isScrubbing) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    setIsScrubbing(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case " ":
      case "k":
        e.preventDefault();
        togglePlay();
        break;
      case "ArrowLeft":
        e.preventDefault();
        seekBy(-5);
        break;
      case "ArrowRight":
        e.preventDefault();
        seekBy(5);
        break;
      case "m":
        toggleMute();
        break;
      case "f":
        toggleFullscreen();
        break;
      default:
        return;
    }
    revealControls();
  };

  const handleProgress = (e: SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    const ranges = video.buffered;
    if (ranges.length) setBuffered(ranges.end(ranges.length - 1));
  };

  const showChrome = controlsVisible || !isPlaying || isScrubbing;

  return (
    <div
      ref={containerRef}
      role="group"
      aria-label={`${title} video`}
      tabIndex={0}
      className={cn(
        "group relative aspect-video select-none overflow-hidden bg-black outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 focus-visible:ring-offset-card",
        !showChrome && "cursor-none",
        className
      )}
      onKeyDown={handleKeyDown}
      onPointerMove={revealControls}
      onPointerLeave={() => isPlaying && setControlsVisible(false)}
    >
      <video
        ref={videoRef}
        key={src}
        className="absolute inset-0 h-full w-full object-cover"
        src={src}
        playsInline
        preload="metadata"
        controlsList="nodownload noplaybackrate noremoteplayback"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        onClick={togglePlay}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        onDurationChange={(e) => setDuration(e.currentTarget.duration || 0)}
        onProgress={handleProgress}
        onVolumeChange={(e) => setIsMuted(e.currentTarget.muted)}
      />

      {/* Paused-state play button */}
      {!isPlaying && (
        <button
          type="button"
          aria-label={currentTime > 0 ? "Resume video" : "Play video"}
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors hover:bg-black/35"
        >
          <span className="flex size-16 items-center justify-center rounded-full bg-brand-600 text-brand-contrast shadow-lg transition-transform group-hover:scale-105 sm:size-20">
            <Play
              aria-hidden="true"
              className="ml-1 size-6 fill-current sm:size-7"
            />
          </span>
        </button>
      )}

      {/* Control bar */}
      <div
        className={cn(
          "absolute inset-x-0 bottom-0 flex flex-col gap-2 bg-gradient-to-t from-black/75 via-black/40 to-transparent px-3 pb-2.5 pt-8 transition-opacity duration-200 sm:px-4",
          showChrome ? "opacity-100" : "pointer-events-none opacity-0"
        )}
      >
        <div
          ref={trackRef}
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(currentTime)}
          aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
          className="group/track relative h-4 cursor-pointer touch-none"
          onPointerDown={handleTrackPointerDown}
          onPointerMove={handleTrackPointerMove}
          onPointerUp={handleTrackPointerUp}
          onPointerCancel={handleTrackPointerUp}
        >
          <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 overflow-hidden rounded-full bg-white/25 transition-[height] group-hover/track:h-1.5">
            <div
              className="absolute inset-y-0 left-0 bg-white/30"
              style={{ width: `${bufferedRatio * 100}%` }}
            />
            <div
              className="absolute inset-y-0 left-0 bg-brand-600"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          <div
            className={cn(
              "absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-600 shadow ring-2 ring-white transition-opacity",
              isScrubbing ? "opacity-100" : "opacity-0 group-hover/track:opacity-100"
            )}
            style={{ left: `${progress * 100}%` }}
          />
        </div>

        <div className="flex items-center gap-1 text-white">
          <button
            type="button"
            aria-label={isPlaying ? "Pause" : "Play"}
            onClick={togglePlay}
            className="flex size-8 items-center justify-center rounded-md transition-colors hover:bg-white/15"
          >
            {isPlaying ? (
              <Pause aria-hidden="true" className="size-4 fill-current" />
            ) : (
              <Play aria-hidden="true" className="ml-0.5 size-4 fill-current" />
            )}
          </button>
          <button
            type="button"
            aria-label={isMuted ? "Unmute" : "Mute"}
            onClick={toggleMute}
            className="flex size-8 items-center justify-center rounded-md transition-colors hover:bg-white/15"
          >
            {isMuted ? (
              <VolumeX aria-hidden="true" className="size-4" />
            ) : (
              <Volume2 aria-hidden="true" className="size-4" />
            )}
          </button>
          <span className="ml-1 text-xs tabular-nums text-white/90">
            {formatTime(currentTime)}
            <span className="text-white/50"> / {formatTime(duration)}</span>
          </span>
          <span className="flex-1" />
          <button
            type="button"
            aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            onClick={toggleFullscreen}
            className="flex size-8 items-center justify-center rounded-md transition-colors hover:bg-white/15"
          >
            {isFullscreen ? (
              <Minimize aria-hidden="true" className="size-4" />
            ) : (
              <Maximize aria-hidden="true" className="size-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
